import React, { useState, useEffect, useCallback, memo } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import { FormattedMessage } from 'react-intl'
import Color from 'color'

// icons
import Wheel from 'common/icons/wheel'
import Reset from 'common/icons/reset'
import ConceptSize from 'common/icons/concept-size'
import Ontologies from 'common/icons/ontologies'

// components
import SelectColors from 'components/SelectColors'
import { ParagraphLegend } from 'components/Paragraphs'
import LegendColor from './LegendColor'
import SelectRelations from 'components/SelectRelations'
import ToggleSwitch from 'components/ToggleSwitch'

// utils
import FormatNumber from 'common/utils/number'

// hooks
import { TitleCaps } from 'components/SelectRelations/MenuContent'

const barHeight = 60
const Centerer = styled.div`
  position: absolute;
  bottom: 0;
  left: 10px; 
  right: 10px;
  display: flex;
  justify-content: center;
  
`
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 670px;
  max-width: 800px;
`
const BottomLegend = styled.div`
  background-color: ${({ theme }) => theme.blueDark};
  height: ${barHeight}px;
  display: flex;
  align-items: stretch;
`
const CommandContainer = styled.div(({ theme }) => css`
  position: absolute;
  bottom: 40px;
  right: 0;
  background: ${theme.white};
  width: 200px;
  padding: 25px 30px;
  border-radius: 2px;
  color: ${theme.blueDark};
`)

const WheelStyled = styled(Wheel)`
  margin-left: 10px;
`

export function GraphCommand (props) {
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const onOutsideClick = () => setIsOpen(false)
    if (isOpen) window.addEventListener('click', onOutsideClick)
    return () => window.removeEventListener('click', onOutsideClick)
  }, [isOpen])

  const stopPropagation = useCallback((e) => {
    e.stopPropagation()
  }, [])

  const onClick = useCallback(() => setIsOpen(!isOpen), [isOpen])

  return (
    <div
      onClick={stopPropagation}
    >
      <WheelStyled onClick={onClick} />
      {
        isOpen && (
          <CommandContainer>
            <TitleCaps>
              <FormattedMessage id='graph-settings-rendering' />
            </TitleCaps>
            <ToggleSwitch
              labels={[
                <FormattedMessage key='off' id='graph-settings-rendering-off' />,
                <FormattedMessage key='on' id='graph-settings-rendering-on' />
              ]}
              isChecked={props.rendering}
              onChange={props.onToggleRendering}
            />
          </CommandContainer>
        )
      }
    </div>
  )
}

export function GraphCameraReset (props) {
  return (
    <Reset onClick={props.onClick} />
  )
}

const LegendContainer = styled.div`
  position: relative;
  width: 100%;
  padding: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => Color(theme.blueDeep).alpha(0.7).toString()};
`

const LegendLeft = styled.div`
  display: flex;
  align-items: center;
`
const ChildrenContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 8px;
`

const LegendText = styled(ParagraphLegend)(({ theme }) => css`
  color: ${Color(theme.white).alpha(theme.labelOpacity).toString()};
  margin-right: 10px;
`)
const ConceptSizeStyled = styled(ConceptSize)(({ theme }) => css`
  fill: ${Color(theme.white).alpha(theme.labelOpacity).toString()};
  margin-right: 10px;
`)
const OntologiesStyled = styled(Ontologies)`
  margin-right: 10px;
`

const TopLegend = ({ conceptsCount, relationsCount, children }) => {
  return (
    <LegendContainer>
      <LegendLeft>
        <LegendText>
          <FormattedMessage
            id='legend-concepts-relations-count'
            values={{
              CONCEPTS: FormatNumber(conceptsCount),
              RELATIONS: FormatNumber(relationsCount)
            }}
          />
        </LegendText>
        <LegendText>
          <ConceptSizeStyled />
          <FormattedMessage id='legend-concept-size' />
        </LegendText>
        <LegendText>
          <OntologiesStyled />
          <FormattedMessage id='legend-ontologies' />
        </LegendText>
      </LegendLeft>
      <ChildrenContainer>
        {children}
      </ChildrenContainer>
    </LegendContainer>
  )
}

const GraphSettingsBar = (props) => {
  return (
    <Centerer>
      <Container>
        <TopLegend
          conceptsCount={props.conceptsCount}
          relationsCount={props.relationsCount}
        >
          {props.children}
        </TopLegend>
        <BottomLegend>
          <SelectRelations count={props.count} />
          <SelectColors />
          <LegendColor />
        </BottomLegend>
      </Container>
    </Centerer>
  )
}
export default memo(GraphSettingsBar)
